import React from 'react';
import { Link } from 'react-router-dom';
import { SearchX, WifiOff, RotateCw } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';

const Bar = ({ className }) => <div className={`rounded-lg bg-slate-200/70 animate-pulse ${className}`} />;

export const CourseDetailSkeleton = () => (
  <main data-testid="course-skeleton" aria-busy="true" className="page-y min-h-screen bg-slate-50/60">
    <div className="site-container">
      <Bar className="h-4 w-56 mb-6" />
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center rounded-3xl bg-white border border-slate-200/70 p-6 sm:p-8">
        <div className="space-y-4">
          <Bar className="h-6 w-28" /><Bar className="h-10 w-4/5" /><Bar className="h-4 w-full" /><Bar className="h-4 w-2/3" />
          <div className="flex gap-3 pt-2"><Bar className="h-12 w-40 rounded-xl" /><Bar className="h-12 w-32 rounded-xl" /></div>
        </div>
        <Bar className="aspect-video w-full rounded-2xl" />
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-8">
        <div className="space-y-3">{[0, 1, 2, 3].map(i => <Bar key={i} className="h-14 w-full rounded-xl" />)}</div>
        <Bar className="h-64 w-full rounded-2xl" />
      </div>
    </div>
  </main>
);

export const LearnSkeleton = () => (
  <main data-testid="course-skeleton" aria-busy="true" className="pt-[5.25rem] pb-10 min-h-screen bg-slate-50/70">
    <div className="mx-auto w-full max-w-[1440px] px-3 sm:px-5 lg:px-6">
      <Bar className="h-4 w-72 mb-4" />
      <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[440px_minmax(0,1fr)]">
        <div className="hidden lg:block space-y-4"><Bar className="h-40 w-full rounded-2xl" /><Bar className="h-96 w-full rounded-2xl" /></div>
        <div className="space-y-4"><Bar className="aspect-video w-full rounded-2xl" /><Bar className="h-8 w-2/3" /><Bar className="h-40 w-full rounded-2xl" /></div>
      </div>
    </div>
  </main>
);

export const CourseLoadState = ({ status, onRetry }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const notFound = status === 'notfound';
  const Icon = notFound ? SearchX : WifiOff;
  return (
    <main data-testid={notFound ? 'course-not-found' : 'course-load-error'} className="pt-32 pb-16 min-h-screen flex items-start justify-center px-4">
      <div className="max-w-md w-full text-center rounded-2xl bg-white border border-slate-200 p-8 shadow-sm">
        <span className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4"><Icon className="w-7 h-7" /></span>
        <h1 className="text-xl font-extrabold text-slate-900">{notFound ? L('الكورس غير موجود', 'Course not found') : L('تعذر تحميل الكورس', 'Could not load the course')}</h1>
        <p className="text-sm text-slate-500 mt-2">{notFound ? L('ربما تم حذف الكورس أو أن الرابط غير صحيح.', 'It may have been removed or the link is wrong.') : L('تحقق من اتصالك بالإنترنت ثم حاول مرة أخرى.', 'Check your connection and try again.')}</p>
        <div className="flex justify-center gap-2 mt-6">
          {!notFound && <button type="button" data-testid="course-retry-btn" onClick={onRetry} className="h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold inline-flex items-center gap-2"><RotateCw className="w-4 h-4" />{L('إعادة المحاولة', 'Retry')}</button>}
          <Link to="/courses" data-testid="course-back-to-list" className="h-10 px-4 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold inline-flex items-center hover:bg-slate-50">{L('كل الكورسات', 'All courses')}</Link>
        </div>
      </div>
    </main>
  );
};
